/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GlossaryItemResponseDto } from '@ink-ai/insight-service-sdk';
import { EllipsisCell } from '../../../common/components/paginated-table/EllipsisCell';
import { TranslationsCell } from '../../../common/components/paginated-table/TranslationsCell';
import { PaginatedTable } from '../../../common/components/paginated-table/PaginatedTable';
import { useDispatch, useSelector } from 'react-redux';
import {
  listGlossaryItemsById,
} from '../../../reducers/glossary';
import { RootState } from '../../../reducers';

interface GlossaryItemTableProps {
  glossaryId: string;
  selectedItems: string[];
  searchTerm: string;
  page: number;
  onPageChange: (page: number) => void;
  onSelectedItemsChange: (selectedItems: string[]) => void;
  isExternal?: boolean;
}

export const GlossaryItemTable: React.FC<GlossaryItemTableProps> = ({
  glossaryId,
  selectedItems,
  searchTerm,
  page,
  onPageChange,
  onSelectedItemsChange,
  isExternal = false,
}) => {
  const dispatch = useDispatch<any>();
  const glossary = useSelector((state: RootState) => state.glossary);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  useEffect(() => {
    if (glossaryId) {
      dispatch(listGlossaryItemsById({ 
        uuid: glossaryId, 
        page: (page + 1).toString(), 
        pageSize: rowsPerPage.toString(),
        searchKeyword: searchTerm || undefined
      }));
    }
  }, [dispatch, glossaryId, page, rowsPerPage, searchTerm]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const currentPageGlossaries = filteredAndPaginatedItems;
      const newSelecteds = currentPageGlossaries.map((g) => g.uuid);
      onSelectedItemsChange(newSelecteds);
    } else {
      onSelectedItemsChange([]);
    }
  };

  const handleClick = (uuid: string) => {
    const selectedIndex = selectedItems.indexOf(uuid);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedItems, uuid);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedItems.slice(1));
    } else if (selectedIndex === selectedItems.length - 1) {
      newSelected = newSelected.concat(selectedItems.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedItems.slice(0, selectedIndex),
        selectedItems.slice(selectedIndex + 1),
      );
    }

    onSelectedItemsChange(newSelected);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    onPageChange(0);
  };

  // Frontend filtering and pagination for internal glossary
  const filteredAndPaginatedItems = useMemo(() => {
    return glossary.glossaryItems || [];
  }, [glossary.glossaryItems]);

  const totalCount = useMemo(() => {
    return glossary.glossaryItemsTotal;
  }, [glossary.glossaryItemsTotal]);

  const { t } = useTranslation();

  const columns = [
    {
      id: 'name',
      label: t('glossary.table.name'),
      render: (item: GlossaryItemResponseDto) => (
        <EllipsisCell content={item.name} maxLines={3} />
      ),
      width: 150,
    },
    // {
    //   id: 'description',
    //   label: t('glossary.table.description'),
    //   render: (item: GlossaryItemResponseDto) => (
    //     <EllipsisCell content={item.desc || '-'} maxLines={3} />
    //   ),
    //   width: 150,
    // },
    // {
    //   id: 'abbreviation',
    //   label: t('glossary.table.abbreviation'),
    //   render: (item: GlossaryItemResponseDto) => (
    //     <EllipsisCell content={item.abbr || '-'} />
    //   ),
    //   width: 150,
    // },
    {
      id: 'translations',
      label: t('glossary.table.translations'),
      render: (item: GlossaryItemResponseDto) => (
        <TranslationsCell translations={item.translations} />
      ),
      width: 150,
    },
  ];

  return (
    <PaginatedTable
      totalCount={totalCount}
      columns={columns}
      data={filteredAndPaginatedItems}
      selectedItems={isExternal ? undefined : selectedItems}
      page={page}
      rowsPerPage={rowsPerPage}
      loading={glossary.loading}
      getItemId={isExternal ? undefined : (item) => item.uuid}
      onSelectAllClick={isExternal ? undefined : handleSelectAllClick}
      onItemClick={isExternal ? undefined : handleClick}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
    />
  );
};
